// Zod DTOs for the `/v1/presets/boards` surface (spec §3.5 "Optional preset
// library" + §6.2 `GET /v1/presets/boards` / `POST /v1/presets/boards/:key/apply`).
//
// A "preset" is NOT a database row and carries NO special authority (spec
// §3.5: "deleting the file changes nothing except that the shortcut
// disappears") — it is a read-only scaffold description shipped as
// `apps/api/presets/boards.json`, validated against
// {@link presetBoardsCatalogueFileSchema} at load time
// (`apps/api/src/services/presets.ts`). Applying one performs ORDINARY
// `ExamBoard`/`Level` creates via the exact same `ExamBoardService`/
// `LevelService` paths P1-03/P1-04 already validate — this file therefore
// only needs to describe the CATALOGUE shape (what a preset offers), not a
// parallel "preset board"/"preset level" persistence model.
import { z } from 'zod';
import { codeSchema, descriptionSchema, titleSchema } from './primitives';
import { defaultTabsSchema, sectionLabelsSchema } from './level';

// ─── One level inside a preset's board ──────────────────────────────────
// Mirrors `LevelCreateRequest` (code/name/description) plus the
// `PATCH .../structure` fields (`sectionLabels`/`defaultTabs`) the AC's
// "incl. defaultTabs" explicitly calls out — a preset level is exactly the
// two calls `POST .../levels` + `PATCH .../structure` would need, described
// declaratively instead of as two separate request bodies.

export const presetLevelSchema = z.object({
  code: codeSchema,
  name: titleSchema,
  description: descriptionSchema,
  // Defaults to `{}` (matches `Level.sectionLabels`'s own DB default) so a
  // preset author can omit per-depth terminology entirely.
  sectionLabels: sectionLabelsSchema.default({}),
  defaultTabs: defaultTabsSchema,
});
export type PresetLevel = z.infer<typeof presetLevelSchema>;

// ─── One catalogue entry (= one board + its levels) ─────────────────────

export const presetBoardEntrySchema = z.object({
  code: codeSchema,
  name: titleSchema,
  description: descriptionSchema,
});
export type PresetBoardEntry = z.infer<typeof presetBoardEntrySchema>;

export const presetBoardCatalogueItemSchema = z.object({
  /** Stable, URL-safe identifier for this preset — the `:key` in
   * `POST /v1/presets/boards/:key/apply`. Free text like `codeSchema` (this
   * is a catalogue lookup key, not a domain uniqueness constraint), but
   * restricted to a URL-path-safe alphabet so it never needs escaping. */
  key: z
    .string()
    .trim()
    .min(1, 'key is required')
    .max(80)
    .regex(/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/, 'key must be lowercase kebab/snake-case'),
  /** Catalogue-entry display name (e.g. "Cambridge") — distinct from
   * `board.name`, which becomes the CREATED `ExamBoard.name` verbatim; kept
   * as two fields so a future preset can offer a catalogue-facing summary
   * name that differs from the literal board name it creates (today they
   * are typically identical). */
  name: titleSchema,
  /** Human-readable summary shown in the catalogue UI, e.g. "Cambridge → O
   * Level, AS, A2 with four default tabs" (spec §3.5's own example). */
  description: descriptionSchema,
  board: presetBoardEntrySchema,
  levels: z.array(presetLevelSchema).min(1, 'A preset must scaffold at least one level'),
});
export type PresetBoardCatalogueItem = z.infer<typeof presetBoardCatalogueItemSchema>;

/** The on-disk shape of `apps/api/presets/boards.json` — a bare envelope
 * around the catalogue array, validated wholesale at load time
 * (`../../apps/api/src/services/presets.ts`'s `loadPresetCatalogue`) so a
 * malformed/hand-edited file fails closed to an EMPTY catalogue (never a
 * 500) rather than serving a partially-invalid scaffold. */
export const presetBoardsCatalogueFileSchema = z.object({
  presets: z.array(presetBoardCatalogueItemSchema),
});
export type PresetBoardsCatalogueFile = z.infer<typeof presetBoardsCatalogueFileSchema>;

// ─── GET /v1/presets/boards response ─────────────────────────────────────

export const presetBoardsCatalogueResponseSchema = z.object({
  items: z.array(presetBoardCatalogueItemSchema),
});
export type PresetBoardsCatalogueResponse = z.infer<typeof presetBoardsCatalogueResponseSchema>;
